import { watch } from 'vue'
import {
  mapKeys,
  camelCase,
  snakeCase,
  transform, // eslint-disable-line
  isEqual,
  isObject // eslint-disable-line
} from 'lodash'

// Merge classes
export const cx = (...args) =>
  [...new Set(
    args
      .flatMap(arg => {
        if (!arg) return []

        if (typeof arg === 'string') return arg.split(' ')

        if (Array.isArray(arg)) return arg

        if (typeof arg === 'object') {
          return Object.entries(arg)
            .filter(([, val]) => val)
            .map(([key]) => key)
        }

        return []
      })
  )].join(' ')

export const
  getFormData = formData => {
    const data = {}
    for (const [key, val] of formData.entries()) data[key] = val
    return data
  },

  setFormData = data => {
    const formData = new FormData()
    for (const key in data) formData.append(key, data[key])
    return formData
  },

  /**
   * Converts object keys to camelCase.
   * @param {Record<string, any>} obj - The object to transform.
   * @returns {Record<string, any>} - A new object with camelCase keys.
   */
  camelizeObjectKeys = obj => mapKeys(obj, (v, k) => camelCase(k)),

  /**
   * Converts object keys to snake_case.
   * @param {Record<string, any>} obj - The object to transform.
   * @returns {Record<string, any>} - A new object with snake_case keys.
   */
  pythonizeObjectKeys = obj => mapKeys(obj, (v, k) => snakeCase(k)),

  /**
   * Converts a Vue ref value change into a Promise that resolves when the ref meets a condition.
   *
   * @param {Object} ref - The Vue ref to watch
   * @param {Object} [options] - Configuration options
   * @param {function} [options.condition] - A function that returns true when the Promise should resolve
   * @param {boolean} [options.immediate=false] - Whether to trigger the watcher immediately
   * @param {number} [options.timeout=0] - Timeout in milliseconds (0 means no timeout)
   * @param {string} [options.timeoutMessage='Timeout waiting for ref value'] - Error message if timeout occurs
   * @returns {Promise} A Promise that resolves with the ref value when condition is met
   * @throws {Error} If timeout occurs before condition is met
   *
   * @example
   * // Basic usage
   * const myRef = ref(null);
   * refToPromise(myRef).then(value => console.log('Got value:', value));
   * myRef.value = 'test'; // Promise resolves
   *
   * @example
   * // With condition and timeout
   * const isLoading = ref(true);
   * refToPromise(isLoading, {
   *   condition: val => val === false,
   *   timeout: 5000
   * }).then(() => console.log('Loading complete'));
   */
  refToPromise = (
    ref,
    {
      condition = val => !!val,
      immediate = false,
      timeout = 0,
      timeoutMessage = 'Timeout waiting for ref value'
    } = {}
  ) => {
    return new Promise((resolve, reject) => {
      let timeoutId

      if (timeout > 0) {
        timeoutId = setTimeout(() => {
          stop()
          reject(new Error(timeoutMessage))
        }, timeout)
      }

      const stop = watch(ref, value => {
        if (condition(value)) {
          stop()
          if (timeoutId) clearTimeout(timeoutId)
          resolve(value)
        }
      }, { immediate })

      // Return cleanup function in case promise is cancelled
      return () => {
        stop()
        if (timeoutId) clearTimeout(timeoutId)
      }
    })
  }

/**
 * Deep diff between two object, using lodash
 *
 * @param  {Object} object Object compared
 * @param  {Object} base Object to compare with
 *
 * @return {Object} Return a new object who represent the diff
 */
// export const diffObjects = (object, base) => {
//   if (!base || !object) return
//   const changes = (object, base) => {
//     return transform(object, function (result, value, key) {
//       if (!isEqual(value, base[key])) {
//         result[key] = (isObject(value) && isObject(base[key])) ? changes(value, base[key]) : value
//       }
//     })
//   }
//   return changes(object, base)
// }
export const diffObjects = (obj1, obj2, compareRef = false) => {
  if (!obj1 || !obj2) return
  return Object.keys(obj1).reduce((result, key) => {
    if (!Object.hasOwn(obj2, key)) {
      result.push(key)
    } else if (isEqual(obj1[key], obj2[key])) {
      const resultKeyIndex = result.indexOf(key)

      if (compareRef && obj1[key] !== obj2[key]) {
        result[resultKeyIndex] = `${key} (ref)`
      } else {
        result.splice(resultKeyIndex, 1)
      }
    }
    return result
  }, Object.keys(obj2))
}

/**
 * Creates a queue runner that:
 * - Executes one task at a time with a fixed delay
 * - Prevents duplicate execution for a group tag
 * - Removes all future calls for the same tag once executed
 */
export const createTaggedQueue = (delay = 200) => {
  const queue = []
  const seenTags = new Set()
  let running = false

  const runQueue = () => {
    if (running) return
    running = true

    const next = () => {
      if (!queue.length) {
        running = false
        return
      }

      const { fn, tag } = queue.shift()

      // Skip if this tag was already processed
      if (seenTags.has(tag)) {
        return next()
      }

      // Mark as processed
      seenTags.add(tag)

      // Run the task
      fn()

      // Delay and continue
      setTimeout(next, delay)
    }

    next()
  }

  return {
    /**
     * Add a function to the queue with a group tag
     * @param {Function} fn - Function to run
     * @param {string|number} tag - Unique tag (e.g. group/page id)
     */
    enqueue (fn, tag) {
      if (seenTags.has(tag)) return
      queue.push({ fn, tag })
      runQueue()
    },

    /**
     * Reset the seenTags so groups can be reprocessed (optional)
     */
    reset () {
      seenTags.clear()
    },

    queue
  }
}
